import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UserModel } from '@data-layer/models/user';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { LA_TIMEZONE_NAME } from '@constants/constants';
import { switchMap, takeUntil } from 'rxjs/operators';
import { VerificationCodeHelper } from '@helpers/verification-code.helper';
import { Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { VerifyPhoneNumberComponent } from '@shared/verify-phone-number/components';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { OrganizationsRepository } from '@app/data-layer/repositories/organizations.repository';
import { ValidationMessageModel } from '@app/data-layer/models/common';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';

@Component({
  selector: 'company-user-info-card',
  templateUrl: './company-user-info-card.component.html',
  styleUrls: ['./company-user-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CompanyUserInfoCardComponent implements OnInit {
  @ViewChild(VerifyPhoneNumberComponent, { static: false }) public verifyModal: VerifyPhoneNumberComponent;
  @ViewChild('hiddeninput', { static: false }) public hiddeninput: ElementRef;
  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public user: UserModel;
  public userMemberRole: enumOrganizationRole;

  public initialUserFormGroupValues: any;
  public userFormGroup: UntypedFormGroup;
  public isChangedPhoneNumber: boolean = false;
  public isEditUser: boolean = false;

  public errors: { [name: string]: IErrorForm };
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public integerInputMap: any = INPUT_TYPES_MAP.INTEGER;
  public isTablet$: Observable<boolean>;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();
  private formattedPhoneNumber: string;

  constructor(
    private fh: FormHelper,
    private fb: UntypedFormBuilder,
    private organizationsRepository: OrganizationsRepository,
    private companiesService: CompaniesService,
    private genericService: GenericService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.isTablet$ = this.genericService.isTablet$;

    this.companiesService
      .getUserData()
      .pipe(
        tap((user) => {
          const firstCompany = this.companiesService.getCompanies()[0];
          this.organizationsRepository
            .getOrganizationMembers(user.defaultOrganizationId || firstCompany?.organizationId, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe((members) => {
              const currentUserMember = members.find((member) => member.user.userId === user?.uid);
              this.userMemberRole = currentUserMember?.role;
              this.cdr.detectChanges();
            });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((user: UserModel) => {
        this.user = user;
        this.createUserForm();

        this.userFormGroup
          .get(['phoneNumber'])
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((phoneNumberObject: any) => {
            if (phoneNumberObject) {
              this.formattedPhoneNumber = phoneNumberObject ? phoneNumberObject.replace(/(\s|-)*/g, '') : null;
              this.isChangedPhoneNumber =
                this.user.phoneNumberConfirmed && phoneNumberObject !== undefined && this.formattedPhoneNumber !== this.user.phoneNumber;
            } else {
              this.formattedPhoneNumber = undefined;
            }
            // fixes phone library issue with [Object object]
            this.cdr.detectChanges();
          });
        this.formattedPhoneNumber = !!this.user?.phoneNumber ? this.user.phoneNumber : null;
      });
  }

  public get phoneNumber(): string {
    const phoneNumberObject: any = this.userFormGroup.get('phoneNumber').value;
    return phoneNumberObject?.number ? phoneNumberObject?.number : phoneNumberObject;
  }

  public get memberRoleName(): string {
    return `LABELS.MEMBER_ROLES.${enumOrganizationRole[this.userMemberRole]}`;
  }

  public saveUser(): void {
    const invalidFields: [{ [key: string]: ValidationMessageModel }] = this.fh.validateAllFields(this.userFormGroup);
    if (this.isEditUser && Object.keys(invalidFields).length > 0) {
      this.openValidationModal(invalidFields);
      return;
    }
    const phoneNumberValue: any = this.userFormGroup.get('phoneNumber').value;
    const timezoneValue: string = this.userFormGroup.get('timeZone').value;
    const phoneNumber: string = phoneNumberValue ? phoneNumberValue.replace(/(\s|-)*/g, '') : null;

    this.companiesService
      .updateUser({
        ...this.userFormGroup.value,
        phoneNumber: phoneNumber,
        timeZone: timezoneValue,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initialUserFormGroupValues = this.userFormGroup.value;
        this.changeIsEditUserState();
      });
  }

  public openVerifyModal(): void {
    if (!this.userFormGroup.get('phoneNumber').invalid && this.formattedPhoneNumber) {
      this.hiddeninput.nativeElement.focus();
      this.updatePhoneNumber();
      this.verify();
    }
  }

  public changeIsEditUserState(isResetForm: boolean = false): void {
    this.isEditUser = !this.isEditUser;
    if (isResetForm && !this.isEditUser) {
      this.resetUserFormToDefault();
    }
    this.cdr.markForCheck();
  }

  public get isVerificationCodeExpired(): boolean {
    if (this.userFormGroup.get('phoneNumber').invalid || !this.userFormGroup.get('phoneNumber').value) {
      return true;
    }
    return VerificationCodeHelper.isVerificationCodeExpired(this.formattedPhoneNumber);
  }

  public get getRandomColor(): string {
    return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  }

  private verify(): void {
    this.verifyModal.openModal(this.formattedPhoneNumber, this.user);

    if (this.isVerificationCodeExpired) {
      this.companiesService
        .phoneConfirmation(this.formattedPhoneNumber)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          VerificationCodeHelper.setVerificationCode(this.formattedPhoneNumber);
        });
    }
  }

  private updatePhoneNumber(): void {
    if (this.formattedPhoneNumber === this.companiesService.getUser().phoneNumber) {
      return;
    }
    this.companiesService
      .updateUser({ ...this.companiesService.getUser(), phoneNumber: this.formattedPhoneNumber })
      .pipe(
        switchMap(() => this.companiesService.getUserData()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private createUserForm(): void {
    this.userFormGroup = this.fb.group({
      email: new UntypedFormControl(this.user ? this.user.email : null, [Validators.required, Validators.maxLength(70)]),
      firstName: new UntypedFormControl(this.user ? this.user.firstName : null, [Validators.required]),
      lastName: new UntypedFormControl(this.user ? this.user.lastName : null, [Validators.required]),
      phoneNumber: new UntypedFormControl(this.user ? this.user.phoneNumber : null, [Validators.maxLength(15)]),
      timeZone: new UntypedFormControl(this.user ? this.user.timeZone : LA_TIMEZONE_NAME, [Validators.required]),
      monthlyNewslettersSubscriptionActivated: new UntypedFormControl(
        this.user ? this.user.monthlyNewslettersSubscriptionActivated : false,
      ),
      advertisingMessagesSubscriptionActivated: new UntypedFormControl(
        this.user ? this.user.advertisingMessagesSubscriptionActivated : false,
      ),
    });
    this.initialUserFormGroupValues = this.userFormGroup.value;
    this.errors = {
      firstName: {
        required: 'FORM_ERROR.REQUIRED',
      },
      lastName: {
        required: 'FORM_ERROR.REQUIRED',
      },
      email: {
        required: 'FORM_ERROR.REQUIRED',
      },
      phoneNumber: {
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
      },
    };
    this.cdr.markForCheck();
  }

  private resetUserFormToDefault() {
    this.userFormGroup.patchValue(this.initialUserFormGroupValues);
  }

  private openValidationModal(data: [{ [key: string]: ValidationMessageModel }]): void {
    const isFirstNameKeyExist = 'firstName' in data;
    const isLastNameKeyExist = 'lastName' in data;

    let modalData = {
      ...data,
    };

    if (isFirstNameKeyExist) {
      modalData['FIRST_NAME'] = data['firstName'];
      delete modalData['firstName'];
    }

    if (isLastNameKeyExist) {
      modalData['LAST_NAME'] = data['lastName'];
      delete modalData['lastName'];
    }

    this.missingFieldModal.openCustomModal(modalData);
  }
}
